import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Plus, Search, Loader2, FileText, Edit, Trash2,
  Send, Copy, Clock, CheckCircle, XCircle,
  MoreHorizontal, Tag, RefreshCw, Globe,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import DesignerLayout from './DesignerLayout';
import SharingPanel from '../../components/shared/SharingPanel';
import TemplateThumbnail from '../../components/shared/TemplateThumbnail';
import LetterTile from '../../components/shared/LetterTile';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_TYPES = [
  'Instagram Post', 'Instagram Story', 'Facebook Post', 'Twitter Post',
  'YouTube Thumbnail', 'Flyer', 'Poster', 'Presentation', 'Resume',
  'Business Card', 'Invitation', 'Banner', 'Brochure', 'Certificate', 'Other',
];

const STATUS_CONFIG = {
  pending:   { label: 'Pending Review', badge: 'bg-amber-100 text-amber-700',    icon: Clock },
  approved:  { label: 'Approved',       badge: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected:  { label: 'Rejected',       badge: 'bg-red-100 text-red-600',        icon: XCircle },
  published: { label: 'Published',      badge: 'bg-blue-100 text-blue-700',      icon: Globe },
};

const STATUS_TABS = ['all', 'draft', 'pending', 'approved', 'rejected', 'published'];

// ─── Actions Dropdown ─────────────────────────────────────────────────────────
const ActionsMenu = ({ template, onEdit, onDelete, onSubmit, onDuplicate, onEditSharing }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const status = template.submission_status || template.status;
  const canSubmit = !status || status === 'rejected';
  const canEdit = template._source !== 'designer_template' || status === 'rejected';

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700"
        onClick={() => setOpen(o => !o)}>
        <MoreHorizontal className="w-4 h-4" />
      </Button>
      {open && (
        <div className="absolute right-0 top-8 z-[9999] bg-white border border-gray-100 rounded-xl shadow-lg py-1 w-48">
          {canEdit && (
            <button onClick={() => { setOpen(false); onEdit(); }}
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
              <Edit className="w-3.5 h-3.5 text-gray-400" />
              {template._source === 'designer_template' ? 'Edit Canvas' : 'Open in Editor'}
            </button>
          )}
          <button onClick={() => { setOpen(false); onDuplicate(); }}
            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
            <Copy className="w-3.5 h-3.5 text-gray-400" /> Duplicate
          </button>
          {template._source === 'designer_template' && (
            <button onClick={() => { setOpen(false); onEditSharing && onEditSharing(); }}
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
              <Edit className="w-3.5 h-3.5 text-gray-400" /> Edit Sharing
            </button>
          )}
          {canSubmit && (
            <button onClick={() => { setOpen(false); onSubmit(); }}
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-violet-700 hover:bg-violet-50">
              <Send className="w-3.5 h-3.5 text-violet-400" />
              {status === 'rejected' ? 'Resubmit' : 'Submit for Review'}
            </button>
          )}
          <div className="border-t border-gray-100 my-1" />
          <button onClick={() => { setOpen(false); onDelete(); }}
            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const DesignerMyTemplates = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');

  // Submit modal state
  const [submitDialog, setSubmitDialog] = useState(null); // source template or null for new
  const [submitting, setSubmitting] = useState(false);

  // Sharing modal state — designer Edit Sharing flow
  const [shareDialog, setShareDialog] = useState(null);    // template doc, or null
  const [shareDraft, setShareDraft] = useState(null);      // working copy of share_settings
  const [shareSaving, setShareSaving] = useState(false);

  const openShareDialog = (t) => {
    setShareDialog(t);
    setShareDraft(t.share_settings || {
      is_public: true,
      allow_use_template: true,
      allow_shared_sessions: true,
      allow_downloads: true,
    });
  };

  const handleSaveSharing = async () => {
    if (!shareDialog || !shareDraft) return;
    setShareSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/designer/templates/${shareDialog.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ share_settings: shareDraft }),
        },
      );
      if (!res.ok) throw new Error(await res.text());
      // Patch the local list so the new settings reflect immediately
      // without a refetch round-trip.
      setTemplates((prev) => prev.map((x) =>
        x.id === shareDialog.id ? { ...x, share_settings: shareDraft } : x,
      ));
      toast({ title: 'Sharing updated' });
      setShareDialog(null);
      setShareDraft(null);
    } catch {
      toast({ title: 'Could not update sharing', variant: 'destructive' });
    } finally {
      setShareSaving(false);
    }
  };
  const [submitForm, setSubmitForm] = useState({
    title: '', description: '', category_id: '', template_type: '',
    tags: '', thumbnail: '', preview_url: '',
  });

  const token = localStorage.getItem('user_token');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tRes, cRes] = await Promise.all([
        fetch(`${API_URL}/api/designer/templates`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/public/categories`),  // public endpoint, no auth needed
      ]);
      if (tRes.ok) {
        const tData = await tRes.json();
        // Surface the actual payload shape per template so designers can verify
        // template_json / pages / thumbnail / preview_url presence in DevTools.
        console.log('[DesignerMyTemplates] /api/designer/templates →', tData.length, 'templates');
        tData.forEach((t, i) => {
          console.log(`  [${i}] id=${t.id} title=${JSON.stringify(t.title || t.name)}`, {
            thumbnail: !!t.thumbnail,
            preview_url: !!t.preview_url,
            template_json: !!t.template_json,
            pages: t.template_json?.pages?.length || 0,
            canvas_width: t.canvas_width,
            canvas_height: t.canvas_height,
          });
        });
        setTemplates(tData);
      }
      if (cRes.ok) setCategories(await cRes.json());
    } catch {
      toast({ title: 'Error', description: 'Failed to load templates', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openSubmitDialog = (template = null) => {
    setSubmitForm({
      title: template?.name || template?.title || '',
      description: template?.description || '',
      category_id: template?.category_id || '',
      template_type: template?.template_type || template?.template_type || '',
      tags: Array.isArray(template?.tags) ? template.tags.join(', ') : '',
      thumbnail: template?.thumbnail || '',
      preview_url: template?.preview_url || '',
    });
    setSubmitDialog(template || { _isNew: true });
  };

  const handleSubmit = async () => {
    if (!submitForm.title.trim()) {
      toast({ title: 'Title required', variant: 'destructive' });
      return;
    }
    const templateJson = submitDialog?.template_json
      || (submitDialog?.pages?.length ? { pages: submitDialog.pages } : null);
    const pages = templateJson?.pages;
    if (!pages?.length) {
      toast({
        title: 'No canvas data',
        description: 'Open this template in the editor, add elements, then submit from there.',
        variant: 'destructive',
      });
      return;
    }
    if (!pages[0]?.elements?.length) {
      toast({
        title: 'First page is empty',
        description: 'Add at least one element to the canvas before submitting.',
        variant: 'destructive',
      });
      return;
    }
    const pageSummary = pages.map((p, i) => ({ index: i, elementCount: p.elements?.length ?? 0 }));
    console.log("Submitting pages:", pages);
    console.log("Page summary:", pageSummary);
    setSubmitting(true);

    if (!API_URL) {
      setSubmitting(false);
      toast({ title: 'Backend not configured', description: 'Set REACT_APP_BACKEND_URL in your .env file', variant: 'destructive' });
      return;
    }

    try {
      const tags = submitForm.tags.split(',').map(t => t.trim()).filter(Boolean);
      const payload = {
        title: submitForm.title.trim(),
        description: submitForm.description.trim(),
        category_id: submitForm.category_id || null,
        template_type: submitForm.template_type,
        tags,
        thumbnail: submitForm.thumbnail,
        preview_url: submitForm.preview_url,
        template_json: submitDialog?.template_json
          || (submitDialog?.pages?.length ? { pages: submitDialog.pages, canvas_size: submitDialog.canvas_size } : null),
        canvas_width: submitDialog?.canvas_width || submitDialog?.canvas_size?.width || 1080,
        canvas_height: submitDialog?.canvas_height || submitDialog?.canvas_size?.height || 1080,
      };

      let res;
      // Resubmit flow: rejected designer_template → save edits first, then resubmit
      if (submitDialog?._source === 'designer_template' && submitDialog?.status === 'rejected') {
        // Step 1: Persist any content edits made in the dialog (title, description, tags, etc.)
        const updatePayload = {
          title: submitForm.title.trim(),
          description: submitForm.description.trim(),
          category_id: submitForm.category_id || undefined,
          template_type: submitForm.template_type || undefined,
          tags,
          thumbnail: submitForm.thumbnail || undefined,
          preview_url: submitForm.preview_url || undefined,
        };
        await fetch(`${API_URL}/api/designer/templates/${submitDialog.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(updatePayload),
        });

        // Step 2: Change status from "rejected" → "pending" (clears feedback)
        res = await fetch(`${API_URL}/api/designer/templates/${submitDialog.id}/resubmit`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        res = await fetch(`${API_URL}/api/designer/templates/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
      }

      if (res.ok) {
        toast({ title: 'Submitted!', description: 'Your template is pending admin review.' });
        setSubmitDialog(null);
        fetchData();
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail || 'Submission failed', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Network error — is the backend running?', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.title || t.name}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/designer/templates/${t.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { toast({ title: 'Deleted' }); fetchData(); }
      else {
        const err = await res.json();
        toast({ title: 'Cannot delete', description: err.detail, variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleDuplicate = async (t) => {
    try {
      const payload = {
        title: `${t.title || t.name} (copy)`,
        description: t.description, category_id: t.category_id,
        template_type: t.template_type, tags: t.tags,
        thumbnail: t.thumbnail, preview_url: t.preview_url,
        template_json: t.template_json,
        canvas_width: t.canvas_width, canvas_height: t.canvas_height,
      };
      const res = await fetch(`${API_URL}/api/designer/templates/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...payload, title: payload.title }),
      });
      if (res.ok) { toast({ title: 'Duplicated' }); fetchData(); }
    } catch {
      toast({ title: 'Error', description: 'Failed to duplicate', variant: 'destructive' });
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return iso; }
  };

  const getStatus = (t) => t.submission_status || t.status || 'draft';
  const getCfg    = (t) => STATUS_CONFIG[getStatus(t)];
  const catName   = (id) => categories.find(c => c.id === id)?.name || '—';
  const count     = (key) => key === 'all' ? templates.length : templates.filter(t => getStatus(t) === key).length;

  const filtered = templates.filter(t => {
    const q = search.toLowerCase();
    const name = (t.title || t.name || '').toLowerCase();
    return name.includes(q) && (activeTab === 'all' || getStatus(t) === activeTab);
  });

  return (
    <DesignerLayout>
      <div className="space-y-6 w-full">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Templates</h1>
            <p className="text-gray-500 text-sm mt-0.5">Manage and submit your design templates.</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" onClick={() => openSubmitDialog()} className="gap-1.5">
              <Send className="w-4 h-4" /> Submit Template
            </Button>
            <Button onClick={() => navigate('/designer/editor')} className="bg-violet-600 hover:bg-violet-700 gap-1.5">
              <Plus className="w-4 h-4" /> Create in Editor
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-4">
          {[
            { label: 'Draft',     value: count('draft') },
            { label: 'Pending',   value: count('pending') },
            { label: 'Approved',  value: count('approved') },
            { label: 'Rejected',  value: count('rejected') },
            { label: 'Published', value: count('published') },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 px-5 py-4">
              <p className="text-2xl font-bold text-gray-900 tabular-nums">{s.value}</p>
              <p className="text-sm text-gray-500 font-medium mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs + Search */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex bg-white border border-gray-200 rounded-xl p-1 gap-1">
            {STATUS_TABS.map(key => (
              <button key={key}
                onClick={() => setActiveTab(key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
                  activeTab === key ? 'bg-violet-600 text-white' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {key === 'all' ? 'All' : key}
                <span className={`ml-1.5 text-xs ${activeTab === key ? 'text-violet-200' : 'text-gray-400'}`}>
                  {count(key)}
                </span>
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search templates…" value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-white border-gray-200" />
          </div>
          <Button variant="ghost" size="sm" onClick={fetchData} className="gap-1.5 text-gray-500">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-7 h-7 animate-spin text-violet-600" />
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide w-16 rounded-tl-2xl">Preview</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Template Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Submitted</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 tracking-wide">Last Updated</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 tracking-wide rounded-tr-2xl">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-12 h-12 bg-violet-100 rounded-2xl flex items-center justify-center">
                          <FileText className="w-6 h-6 text-violet-500" />
                        </div>
                        <p className="font-semibold text-gray-900">
                          {search ? 'No templates match' : 'No templates yet'}
                        </p>
                        {!search && (
                          <div className="flex items-center gap-2 mt-1">
                            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openSubmitDialog()}>
                              <Send className="w-3.5 h-3.5" /> Submit Template
                            </Button>
                            <Button size="sm" className="bg-violet-600 hover:bg-violet-700 gap-1.5" onClick={() => navigate('/designer/editor')}>
                              <Plus className="w-3.5 h-3.5" /> Create in Editor
                            </Button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : filtered.map(t => {
                  const cfg = getCfg(t);
                  const StatusIcon = cfg?.icon || Clock;
                  return (
                    <tr key={t.id} className="hover:bg-gray-50/50 transition-colors group">
                      <td className="px-5 py-3.5">
                        <div className="relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                          <TemplateThumbnail
                            template={t}
                            fallback={<LetterTile label={t.title || t.name || 'U'} />}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <p className="font-semibold text-gray-900 truncate max-w-[220px]">{t.title || t.name}</p>
                        {getStatus(t) === 'rejected' && (t.review_notes || t.feedback || t.rejection_reason) && (
                          <p className="text-xs text-red-500 mt-1 max-w-[220px] leading-snug">
                            <span className="font-semibold">Reason:</span>{' '}
                            {t.review_notes || t.feedback || t.rejection_reason}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-gray-500 text-xs">{catName(t.category_id)}</td>
                      <td className="px-4 py-3.5 text-gray-500 text-xs">{t.template_type || '—'}</td>
                      <td className="px-4 py-3.5">
                        {cfg ? (
                          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${cfg.badge}`}>
                            <StatusIcon className="w-3 h-3" /> {cfg.label}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Draft</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-gray-500 text-xs tabular-nums">
                        {formatDate(t.submitted_at || t.created_at)}
                      </td>
                      <td className="px-4 py-3.5 text-gray-500 text-xs tabular-nums">
                        {formatDate(t.updated_at || t.submitted_at || t.created_at)}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex justify-end">
                          <ActionsMenu
                            template={t}
                            onEditSharing={() => openShareDialog(t)}
                            onEdit={() => {
                              if (t._source === 'designer_template') {
                                const pages = t.template_json?.pages;
                                if (!pages?.length) {
                                  toast({
                                    title: 'No design data',
                                    description: 'This template has no canvas content. Please re-publish from the editor.',
                                    variant: 'destructive',
                                  });
                                  return;
                                }
                                navigate('/designer/editor', {
                                  state: {
                                    template: t,
                                    canvasSize: { width: t.canvas_width || 1080, height: t.canvas_height || 1080 },
                                    templateTypeId: t.template_type_id || null,
                                  },
                                });
                              } else {
                                navigate(t.id ? `/designer/editor/${t.id}` : '/designer/editor');
                              }
                            }}
                            onDelete={() => handleDelete(t)}
                            onSubmit={() => openSubmitDialog(t)}
                            onDuplicate={() => handleDuplicate(t)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Submit / Resubmit Dialog ──────────────────────────────────────── */}
      <Dialog open={!!submitDialog} onOpenChange={() => setSubmitDialog(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-violet-500" />
              {submitDialog?.status === 'rejected' ? 'Resubmit Template' : 'Submit Template for Review'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Info banner */}
            <div className="bg-violet-50 border border-violet-100 rounded-xl px-4 py-3 text-xs text-violet-700 space-y-1">
              <p className="font-semibold">Before you submit:</p>
              <ul className="list-disc list-inside space-y-0.5 text-violet-600">
                <li>Fill in all required fields marked with *</li>
                <li>Preview images are generated automatically from the canvas</li>
                <li>Once submitted, editing is locked until admin review is complete</li>
              </ul>
            </div>

            {/* Row 1: Title + Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Template Title *</Label>
                <Input
                  value={submitForm.title}
                  onChange={e => setSubmitForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g., Summer Sale Instagram Post"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Template Type</Label>
                <Select value={submitForm.template_type} onValueChange={v => setSubmitForm(f => ({ ...f, template_type: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label>Description</Label>
              <textarea
                value={submitForm.description}
                onChange={e => setSubmitForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Describe the template purpose, style, and best use cases…"
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
              />
            </div>

            {/* Category + Tags */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={submitForm.category_id} onValueChange={v => setSubmitForm(f => ({ ...f, category_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    {categories.length === 0 && (
                      <SelectItem value="" disabled>No categories available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tags</Label>
                <div className="relative">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <Input
                    value={submitForm.tags}
                    onChange={e => setSubmitForm(f => ({ ...f, tags: e.target.value }))}
                    placeholder="sale, summer, minimal"
                    className="pl-8"
                  />
                </div>
                <p className="text-[11px] text-gray-400">Comma-separated</p>
              </div>
            </div>

            {/* Rejection feedback (resubmit flow) */}
            {submitDialog?.status === 'rejected' && submitDialog?.review_notes && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <p className="text-xs font-semibold text-red-700 mb-1">Previous rejection reason:</p>
                <p className="text-sm text-red-600 italic">"{submitDialog.review_notes}"</p>
                <p className="text-xs text-red-500 mt-1.5">Please address this feedback before resubmitting.</p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSubmitDialog(null)} disabled={submitting}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || !submitForm.title.trim()}
              className="bg-violet-600 hover:bg-violet-700 gap-1.5">
              {submitting
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
                : <><Send className="w-4 h-4" /> {submitDialog?.status === 'rejected' ? 'Resubmit' : 'Submit for Review'}</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sharing edit modal — designer can toggle share_settings without
          leaving the list. Calls PUT /api/designer/templates/{id} with
          just the share_settings payload (partial update merges
          server-side via dotted-path $set, per Phase C task 13). */}
      <Dialog open={!!shareDialog} onOpenChange={(open) => { if (!open) { setShareDialog(null); setShareDraft(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Sharing</DialogTitle>
          </DialogHeader>
          {shareDraft && (
            <SharingPanel
              value={shareDraft}
              onChange={setShareDraft}
              disabled={shareSaving}
            />
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShareDialog(null); setShareDraft(null); }}
              disabled={shareSaving}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveSharing} disabled={shareSaving}>
              {shareSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DesignerLayout>
  );
};

export default DesignerMyTemplates;
